import React from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';

/**
 * "As of" single-date picker — the primary Stock Value control. A snapshot is a
 * point in time, NOT a range; a range picker here is the #1 thing the spec says
 * to avoid. Defaults to today.
 */
export const AsOfDatePicker: React.FC<{
  value: Date;
  onChange: (date: Date) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation('inventory');
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-muted-foreground">
        {t('reporting.v2.asOf', 'As of')}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-2 font-normal">
            <CalendarIcon className="h-4 w-4" aria-hidden="true" />
            {format(value, 'MMM d, yyyy')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            onSelect={(date) => {
              if (date) {
                onChange(date);
                setOpen(false);
              }
            }}
            disabled={(date) => date > new Date() || date < new Date('2020-01-01')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
        {t('reporting.v2.valuedAtWac', 'Valued at WAC')}
      </span>
    </div>
  );
};

/**
 * The waterfall's OWN date-range control, scoped to the value-movement card so
 * it never governs the snapshot. Presets + custom range, mirroring the global
 * date filter idiom.
 */
export const WaterfallRangeControl: React.FC<{
  preset: string;
  onPresetChange: (v: string) => void;
  range: DateRange | undefined;
  onRangeChange: (r: DateRange | undefined) => void;
}> = ({ preset, onPresetChange, range, onRangeChange }) => {
  const { t } = useTranslation('inventory');

  return (
    <div className="flex items-center gap-2">
      <Select value={preset} onValueChange={onPresetChange}>
        <SelectTrigger className="h-8 w-[130px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7">{t('reporting.filters.last7', 'Last 7 days')}</SelectItem>
          <SelectItem value="30">{t('reporting.filters.last30', 'Last 30 days')}</SelectItem>
          <SelectItem value="90">{t('reporting.filters.last90', 'Last 90 days')}</SelectItem>
          <SelectItem value="custom">{t('reporting.filters.custom', 'Custom')}</SelectItem>
        </SelectContent>
      </Select>
      {preset === 'custom' && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-8 gap-2 text-xs font-normal',
                !range?.from && 'text-muted-foreground',
              )}
            >
              <CalendarIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {range?.from && range?.to
                ? `${format(range.from, 'MMM d')} to ${format(range.to, 'MMM d')}`
                : t('reporting.filters.pickRange', 'Pick a range')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={range}
              defaultMonth={range?.from ?? subDays(new Date(), 30)}
              onSelect={onRangeChange}
              numberOfMonths={2}
              disabled={(date) => date > new Date() || date < new Date('2020-01-01')}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
